/**
 * import react
 */
import React from 'react';
import PropTypes from 'prop-types';

/**
 * import material-ui
 */
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';

/**
 * import helper libraries
 */
import {TimeSeries, TimeRange} from 'pondjs';
import {ChartContainer, ChartRow, Charts, YAxis, LineChart, ScatterChart, Resizable, Legend, styler} from 'react-timeseries-charts';

/**
 * import utils
 */
import AppActions from '../../../actions/app-actions';

/**
 * import components
 */
import DeviceExtendedTsdbInfo from './device-extended-tsdb-info';

/**
 * import fake data
 */
// import fakeData from './fake-device-status-tsdb.json';

/** Global values to this component */
const live = 1;
// const online = 2;
const reboot = 3;

const alarmStatus = {
  info: 0,
  warn: 1,
  error: 2
};

const yAxisProp = {
  min: 1,
  max: 5,
  tickCount: 5,
  label: {'1': '', '2': 'INFO', '3': 'WARNING', '4': 'ERROR', '5': ''},
  heartBeatValue: {1: 5, 2: 1, 3: 1},
  alarmValue: {'info': 2, 'warn': 3, 'error': 4},
  actionValue: 5,
  confValue: 5,
  lConfValue: 5
};

const style = styler([
  {key: 'heartbeat', color: 'black', width: 1.5},
  {key: 'infoAlarms', color: 'dodgerblue'},
  {key: 'warnAlarms', color: 'orange'},
  {key: 'errorAlarms', color: 'red'},
  {key: 'action', color: 'seagreen'},
  {key: 'conf', color: 'indigo'},
  {key: 'lconf', color: 'brown'}
]);

export default class DeviceTimeLineStatus extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = this._getInitialState();

    /** local property */
    this._unmountError = 'Component unmounted';
    this._minTimeRange = 0;
    this._maxTimeRange = 0;
  }

  componentDidMount() {
    this._isMounted = true;
    this._getData();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  _getInitialState() {
    return {
      loading: true,
      heartBeatTimeSeries: new TimeSeries({name: 'x-axis-time-empty', columns: ['time', 'heartbeat'], points: []}),
      infoAlarmsTimeSeries: new TimeSeries({name: 'Info-Alarm-empty', columns:['time', 'infoAlarms'], points: []}),
      warnAlarmsTimeSeries: new TimeSeries({name: 'Warn-Alarm-empty', columns:['time', 'warnAlarms'], points: []}),
      errorAlarmsTimeSeries: new TimeSeries({name: 'Error-Alarm-empty', columns:['time', 'errorAlarms'], points: []}),
      actionTimeSeries: new TimeSeries({name: 'Action-empty', columns:['time', 'action'], points: []}),
      confTimeSeries: new TimeSeries({name: 'Config-empty', columns:['time', 'config'], points: []}),
      lConfTimeSeries: new TimeSeries({name: 'Lconfig-empty', columns:['time', 'lconfig'], points: []}),
      currentTimeWindow: this._getLastHourTimeWindow().time
    };
  }

  _toHeartBeatTimeSeries(tsData=[]) {
    const timePoints = [];
    let prevData = null;

    tsData.forEach(({series=[]}) => {
      series.forEach(({values=[]}) => {
        const len = values.length;
        for (let i=0; i<len; i++) {
          const [time, state] = values[i];
          const timeAsUnix = new Date(time).getTime();
          const yAxisVal = yAxisProp.heartBeatValue[state];
          const [prevTimeAsUnix, prevYAxisVal] = prevData || [];
    
          //push the first time point
          if (!i) {
            timePoints.push([timeAsUnix, yAxisVal]);
            prevData = [timeAsUnix, yAxisVal];
            continue;
          }
          
          //push time point when the state change(on to network live/reboot and vice versa)
          if (prevYAxisVal && prevYAxisVal !== yAxisVal) {
            if (prevYAxisVal === yAxisProp.heartBeatValue[live]) {
              timePoints.push([prevTimeAsUnix, prevYAxisVal]);
              timePoints.push([prevTimeAsUnix, yAxisProp.heartBeatValue[reboot]]);
            } else {
              timePoints.push([timeAsUnix, yAxisProp.heartBeatValue[reboot]]);
              timePoints.push([timeAsUnix, yAxisVal]);
            }
          }
    
          //push the last time point
          if (i === (len-1)) {
            timePoints.push([timeAsUnix, yAxisVal]);
          }
          prevData = [timeAsUnix, yAxisVal];
        }
      });
    });

    return {
      heartBeatTimeSeries: new TimeSeries({name: 'x-axis-time', columns: ['time', 'heartbeat'], points: timePoints})
    };
  }

  _alarmByInfo(module=[]) {
    return module.length > 1 && module[1].status === alarmStatus.info;
  }

  _alarmByWarn(module=[]) {
    return module.length > 1 && module[1].status === alarmStatus.warn;
  }

  _alarmByError(module=[]) {
    return module.length > 1 && module[1].status === alarmStatus.error;
  }

  _filterAlarmsByModuleId(alarms=[]) {
    const tempMap = {};
    alarms.forEach(({module, status}) => {
      let m = tempMap[module] = tempMap[module] || {module, status: 0};
      if (m.status < status) {
        m.status = status;
      }
    });
    return Object.entries(tempMap);
  }

  _getAlarmModuleIdAndType(alarms=[]) {
    const modules = this._filterAlarmsByModuleId(alarms);
    return [
      modules.find(this._alarmByInfo),
      modules.find(this._alarmByWarn),
      modules.find(this._alarmByError)
    ];
  }

  _toAlarmsTimeSeries(tsData=[]) {
    const infoTimePoints = [];
    const warnTimePoints = [];
    const errorTimePoints = [];

    tsData.forEach(({series=[]}) => {
      series.forEach(({values=[]}) => {
        values.forEach(([time, alarms='']) => {
          alarms = alarms === 'null' ? '[]' : alarms;
          const timeAsUnix = new Date(time).getTime();
          const [info, warn, error] = this._getAlarmModuleIdAndType(JSON.parse(alarms));
          if(info) {
            infoTimePoints.push([timeAsUnix, yAxisProp.alarmValue.info]);
          }
          if(warn) {
            warnTimePoints.push([timeAsUnix, yAxisProp.alarmValue.warn]);
          }
          if(error) {
            errorTimePoints.push([timeAsUnix, yAxisProp.alarmValue.error]);
          }
        });
      })
    });

    return {
      infoAlarmsTimeSeries : new TimeSeries({name: 'Info-Alarm', columns:['time', 'infoAlarms'], points: infoTimePoints}),
      warnAlarmsTimeSeries : new TimeSeries({name: 'Warn-Alarm', columns:['time', 'warnAlarms'], points: warnTimePoints}),
      errorAlarmsTimeSeries : new TimeSeries({name: 'Error-Alarm', columns:['time', 'errorAlarms'], points: errorTimePoints})
    };
  }

  _toActionTimeSeries(tsData=[]) {
    const actionTimePoints = [];

    tsData.forEach(({series=[]}) => {
      series.forEach(({values=[]}) => {
        values.forEach(([time]) => {
          const timeAsUnix = new Date(time).getTime();
          actionTimePoints.push([timeAsUnix, yAxisProp.actionValue]);
        });
      });
    });

    return {
      actionTimeSeries: new TimeSeries({name:'Action', columns:['time', 'action'], points: actionTimePoints})
    };
  }

  _toConfTimeSeries(tsData=[]) {
    const confTimePoints = [];

    tsData.forEach(({series=[]}) => {
      series.forEach(({values=[]}) => {
        values.forEach(([time]) => {
          const timeAsUnix = new Date(time).getTime();
          confTimePoints.push([timeAsUnix, yAxisProp.confValue]);
        });
      });
    });

    return {
      confTimeSeries: new TimeSeries({name:'Conf', columns:['time', 'conf'], points: confTimePoints})
    };
  }

  _toLconfTimeSeries(tsData=[]) {
    const lConfTimePoints = [];

    tsData.forEach(({series=[]}) => {
      series.forEach(({values=[]}) => {
        values.forEach(([time]) => {
          const timeAsUnix = new Date(time).getTime();
          lConfTimePoints.push([timeAsUnix, yAxisProp.lConfValue]);
        });
      });
    });

    return {
      lConfTimeSeries: new TimeSeries({name:'lConf', columns:['time', 'lconf'], points: lConfTimePoints})
    };
  }

  _getTimeInMSec() {

  }

  _getData(time='1h', timeInMSec=60 * 60 * 1000) {
    const deviceId = this.props.device.id;
    this._maxTimeRange = Date.now();
    this._minTimeRange = this._maxTimeRange - timeInMSec;
    Promise.all([
      AppActions.getHeartBeatFromTsdb(deviceId, time),
      AppActions.getAlarmsFromTsdb(deviceId, time),
      AppActions.getActionsFromTsdb(deviceId, time),
      AppActions.getConfigmapFromTsdb(deviceId, time),
      AppActions.getLConfFromTsdb(deviceId, time)])
      .then((results) => {
        if(!this._isMounted) {
          throw new Error(this._unmountError);
        }
        return results;
      })
      .then((results) => {
        this.setState({
          loading: false,
          ...this._toHeartBeatTimeSeries(results[0].results),
          ...this._toAlarmsTimeSeries(results[1].results),
          ...this._toActionTimeSeries(results[2].results),
          ...this._toConfTimeSeries(results[3].results),
          ...this._toLconfTimeSeries(results[4].results)
        });
      })
      .catch((error) => {
        if (error.message !== this._unmountError) {
          //const errorMsg = (error.error && error.error.message) || 'Please check your connection.';
          this.setState({...this._getInitialState(), loading: false}, () => {
            // not required now
            //AppActions.setSnackbar(errorMsg, 5000, '');
          });
        }
      });
  }

  _getLastHourTimeWindow() {
    return {txt: 'Last Hour', time: '1h', timeInMSec: 60 * 60 * 1000};
  }

  _getLastDayTimeWindow() {
    return {txt: 'Last Day', time: '1d', timeInMSec: 24 * 60 * 60 * 1000}
  }

  _getLastWeekTimeWindow() {
    return {txt: 'Last Week', time: '7d', timeInMSec: 7 * 24 * 60 * 60 * 1000};
  }

  _getLastHourData = () => {
    const {time, timeInMSec} = this._getLastHourTimeWindow();
    if (this.currentTimeWindow !== time) {
      this.setState({...this._getInitialState(), currentTimeWindow: time}, () => {
        this._getData(time, timeInMSec);
      });
    }
  }

  _getLastDayData = () => {
    const {time, timeInMSec} = this._getLastDayTimeWindow();
    if (this.currentTimeWindow !== time) {
      this.setState({...this._getInitialState(), currentTimeWindow: time}, () => {
        this._getData(time, timeInMSec);
      });
    }
  }

  _getLastWeekData = () => {
    const {time, timeInMSec} = this._getLastWeekTimeWindow();
    if (this.currentTimeWindow !== time) {
      this.setState({...this._getInitialState(), currentTimeWindow: time}, () => {
        this._getData(time, timeInMSec);
      });
    }
  }

  _getYAxisLabel = (val) => {
    return yAxisProp.label[val];
  }

  _getTitle() {
    return (
      <h4>Device Timeline Status</h4>
    );
  }

  _getLegends() {
    return (
      <Legend 
        type='dot'
        style={style}
        categories={[
          {key: 'heartbeat', label: 'Heartbeat', symbolType: 'line'},
          {key: 'action', label: 'Action'},
          {key: 'conf', label: 'Configurations'},
          {key: 'lconf', label: 'Local Configurations'}
        ]}
        marginBottom='0px'
      />      
    );
  }

  _getActionsList() {

    const list = [
      {handler: this._getLastHourData, timeWindow: this._getLastHourTimeWindow()},
      {handler: this._getLastDayData, timeWindow: this._getLastDayTimeWindow()},
      {handler: this._getLastWeekData, timeWindow: this._getLastWeekTimeWindow()}
    ];

    return (
      <Grid container spacing={2} justify='flex-end'>
        {list.map((item={}) => {
          const {handler=()=>{}, timeWindow={}} = item;
          const color = timeWindow.time === this.state.currentTimeWindow ? 'primary' : '';
          return (
            <Grid item key={timeWindow.txt}>
              <Button style={{textTransform: 'none'}} size='small' color={color} onClick={handler}>{timeWindow.txt}</Button>
            </Grid>
          );
        })}
      </Grid>
    );
  }

  _getCharts() {
    const {heartBeatTimeSeries} = this.state;
    if (!heartBeatTimeSeries.count()) {
      return (
        <>
          <Typography align='center'>Records not found</Typography>
          <Grid container alignItems='center'>
            <Grid item xs={12}>{this._getActionsList()}</Grid>
          </Grid>
        </>
      );
    } else {
      const {infoAlarmsTimeSeries, warnAlarmsTimeSeries, errorAlarmsTimeSeries, actionTimeSeries, confTimeSeries, lConfTimeSeries} = this.state;
      const timeRange = new TimeRange(this._minTimeRange, this._maxTimeRange);
      return (
        <>
          <Resizable>
            <ChartContainer timeRange={timeRange} showGridPosition='under'>
              <ChartRow>
                <YAxis 
                  id='status'
                  min={yAxisProp.min}
                  max={yAxisProp.max}
                  hideAxisLine={true}
                  tickCount={yAxisProp.tickCount}
                  showGrid={true}
                  format={this._getYAxisLabel}
                />
                <Charts>
                  <LineChart 
                    style={style}
                    axis='status'
                    series={heartBeatTimeSeries}
                    columns={['heartbeat']}
                  />
                  <ScatterChart
                    style={style}
                    axis='status'
                    series={infoAlarmsTimeSeries}
                    columns={['infoAlarms']}
                    radius={() => 2.5}
                  />
                  <ScatterChart
                    style={style}
                    axis='status'
                    series={warnAlarmsTimeSeries}
                    columns={['warnAlarms']}
                    radius={() => 2.5}
                  />
                  <ScatterChart
                    style={style}
                    axis='status'
                    series={errorAlarmsTimeSeries}
                    columns={['errorAlarms']}
                    radius={() => 2.5}
                  />
                  <ScatterChart
                    style={style}
                    axis='status'
                    series={actionTimeSeries}
                    columns={['action']}
                    radius={() => 2.5}
                  />
                  <ScatterChart
                    style={style}
                    axis='status'
                    series={confTimeSeries}
                    columns={['conf']}
                    radius={() => 2.5}
                  />
                  <ScatterChart
                    style={style}
                    axis='status'
                    series={lConfTimeSeries}
                    columns={['lconf']}
                    radius={() => 2.5}
                  />                  
                </Charts>
                <YAxis 
                  id='now'
                  style={{axis: {stroke: 'green', 'stroke-dasharray': 6, 'stroke-width': 4}, 
                    values: {'font-size': 16, stroke: 'green'}}}
                  min={yAxisProp.min}
                  max={yAxisProp.max}
                  tickCount={yAxisProp.tickCount}
                  format={(val) => (val === 1 ? 'Now' : '')}
                />                  
              </ChartRow>
            </ChartContainer>
          </Resizable>
          <Grid container alignItems='center' spacing={1}>
            <Grid item xs={6}>{this._getLegends()}</Grid>
            <Grid item xs={6}>{this._getActionsList()}</Grid>
            <Grid item xs={12}><DeviceExtendedTsdbInfo {...this.props}/></Grid>
          </Grid>
        </>
      );
    }
  }

  _getLoading() {
    return (
      <Typography align='center'>Please wait, timeline is loading...</Typography>
    );
  }

  _getContent() {
    if(this.state.loading) {
      return this._getLoading();
    } else {
      return this._getCharts();
    }
  }

  render() {
    return (
      <>
        {this._getTitle()}
        {this._getContent()}
      </>
    );

  }
}

DeviceTimeLineStatus.propTypes = {
  deviceId: PropTypes.string.isRequired
};
